import React, { useState } from "react";
import { Navbar, Container, Nav, NavDropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";
import NotificationModal from "./NotificationModal";
import ProfileModal from "./ProfileModal";
import MustChangePasswordModal from "./MustChangePasswordModal";
import { useI18n } from "../i18n";

const NavigationBar: React.FC = () => {
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const { t, lang, setLang } = useI18n();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showMustChange, setShowMustChange] = useState(false);

  const languages = [
    { code: "en", label: "EN", flag: "🇬🇧" },
    { code: "it", label: "IT", flag: "🇮🇹" },
  ];

  React.useEffect(() => {
    if (user?.must_change_password) {
      setShowMustChange(true);
    }
  }, [user?.must_change_password]);

  // Listen for custom event to open notifications modal from anywhere
  React.useEffect(() => {
    const handleOpenNotifications = () => {
      setShowNotifications(true);
    };
    window.addEventListener('openNotifications', handleOpenNotifications);
    return () => window.removeEventListener('openNotifications', handleOpenNotifications);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <>
      <Navbar bg="dark" variant="dark" expand="lg" className="mb-4">
        <Container fluid>
          <Navbar.Brand href="/dashboard">
            <i className="bi bi-airplane"></i> {t("nav.brand")}
          </Navbar.Brand>
          
          {/* Notification bell - always visible on the right */}
          <div className="d-flex align-items-center order-lg-2 ms-auto me-2">
            <Nav.Link
              onClick={() => setShowNotifications(true)}
              className="position-relative p-2"
            >
              <i
                className="bi bi-bell-fill"
                style={{ fontSize: "1.2rem" }}
              ></i>
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount}</span>
              )}
            </Nav.Link>
          </div>
          
          <Navbar.Toggle aria-controls="basic-navbar-nav" className="order-lg-3" />
          <Navbar.Collapse id="basic-navbar-nav" className="order-lg-1">
            <Nav className="ms-auto">
              <Nav.Link href="/dashboard">
                <i className="bi bi-speedometer2"></i> {t("nav.dashboard")}
              </Nav.Link>
              {user?.is_admin && (
                <Nav.Link href="/admin">
                  <i className="bi bi-shield-lock"></i> {t("nav.admin")}
                </Nav.Link>
              )}
              <NavDropdown
                align="end"
                title={
                  <>
                    <span role="img" aria-label="language" className="me-1">
                      {languages.find((l) => l.code === lang)?.flag || "🌐"}
                    </span>
                    {lang.toUpperCase()}
                  </>
                }
                id="language-dropdown"
              >
                {languages.map(({ code, label, flag }) => (
                  <NavDropdown.Item
                    key={code}
                    active={lang === code}
                    onClick={() => setLang(code)}
                  >
                    <span role="img" aria-label={code} className="me-2">
                      {flag}
                    </span>
                    {label}
                  </NavDropdown.Item>
                ))}
              </NavDropdown>
              <NavDropdown
                title={
                  <>
                    <i className="bi bi-person-circle"></i> {user?.username}
                  </>
                }
                id="user-nav-dropdown"
                align="end"
              >
                <NavDropdown.Item onClick={() => setShowProfile(true)}>
                  <i className="bi bi-gear"></i> {t("nav.settings")}
                </NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item onClick={handleLogout}>
                  <i className="bi bi-box-arrow-right"></i> {t("nav.logout")}
                </NavDropdown.Item>
              </NavDropdown>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <NotificationModal
        show={showNotifications}
        onHide={() => setShowNotifications(false)}
      />
      <ProfileModal show={showProfile} onHide={() => setShowProfile(false)} />
      <MustChangePasswordModal
        show={!!user?.must_change_password && showMustChange}
        onHide={() => setShowMustChange(false)}
      />
    </>
  );
};

export default NavigationBar;
